import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { usePortalAuth } from '../../context/PortalAuth';
import { usePortalOrg, orgName } from '../../context/PortalOrg';
import { ROLE_LABELS } from '../../lib/portalPermissions';
import { ProductTag } from './ProductTag';

/**
 * Switches the organisation being worked in. Each row states the person's role
 * there, since the same person can be an owner in one and a viewer in another.
 */
export function OrgSwitcher() {
  const {
    memberships
  } = usePortalAuth();
  const {
    orgId,
    role,
    setOrgId
  } = usePortalOrg();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => !ref.current?.contains(event.target as Node) && setOpen(false);
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  if (!orgId) return null;
  const choose = (next: string) => {
    setOrgId(next);
    setOpen(false);
    // Stay on the same kind of page in the new organisation, where one exists.
    const section = location.pathname.match(/^\/portal\/orgs\/[^/]+\/([^/]+)/)?.[1];
    if (section) navigate(`/portal/orgs/${next}/${section === 'projects' ? 'projects' : section}`);
  };
  return <div ref={ref} className="relative" data-id="element-1017" data-mp="3e4eh4ggbgbs">
      <button type="button" onClick={() => setOpen(v => !v)} aria-haspopup="listbox" aria-expanded={open} className="flex w-full items-center gap-3 rounded-sm border border-white/12 px-3 py-2.5 text-left transition-colors duration-150 hover:border-white/35" data-id="element-1018" data-mp="3ct7kvrplsgg">
        <span className="min-w-0 flex-1" data-id="element-1019" data-mp="10scmttres1s4">
          <span className="block truncate text-[14px] font-medium text-white" data-id="element-1020" data-mp="10te084vwd0d0">{orgName(orgId)}</span>
          <span className="block text-[12px] text-white/55" data-id="element-1021" data-mp="vs821rjgwkw3">{role ? ROLE_LABELS[role] : ''}</span>
        </span>
        {memberships.length > 1 && <ChevronsUpDownIcon className="h-4 w-4 shrink-0 text-white/55" aria-hidden="true" data-id="element-1022" data-mp="qw9do3y532vo" />}
      </button>

      <AnimatePresence data-id="element-1023" data-mp="muedj2ry2vjh">
        {open && memberships.length > 1 && <motion.ul role="listbox" aria-label="Organisations" initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute inset-x-0 top-full z-30 mt-2 overflow-hidden rounded-md border border-line bg-panel p-1.5 shadow-2xl" data-id="element-1024" data-mp="i0ju80g94ikx">
            {memberships.map(m => <li key={m.orgId} role="option" aria-selected={m.orgId === orgId} data-id="element-1025" data-mp="2apar8r0m8b09">
                <button type="button" onClick={() => choose(m.orgId)} className="flex w-full items-start gap-3 rounded-sm px-3 py-2.5 text-left transition-colors duration-150 hover:bg-charcoal" data-id="element-1026" data-mp="2eredmaotp00b">
                  <span className="min-w-0 flex-1" data-id="element-1027" data-mp="2onr6j6cjn9u0">
                    <span className="block text-[14px] text-ink" data-id="element-1028" data-mp="1r3ehev8qshec">{orgName(m.orgId)}</span>
                    <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-1029" data-mp="1w0o9kinin75v">
                      <span className="text-[12px] text-muted" data-id="element-1030" data-mp="21011hht8v4fb">{ROLE_LABELS[m.role]}</span>
                      {m.productIds.map(id => <ProductTag key={id} productId={id} data-id="element-1031" data-mp="25skas2kwfdeb" />)}
                    </span>
                  </span>
                  {m.orgId === orgId && <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1032" data-mp="1da6n2vta17sy" />}
                </button>
              </li>)}
          </motion.ul>}
      </AnimatePresence>
    </div>;
}